import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { FileUp, Upload, Download, RefreshCw, X, Check, CheckCircle, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/services/api';

/**
 * CSVImportModal — bulk lead upload. Backend is at
 * POST /leads/chatbot/{cid}/import-csv. Template downloadable from
 * GET /leads/chatbot/{cid}/csv-template.
 */
export const CSVImportModal = ({ open, onClose, chatbotId, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.csv')) {
        toast.error('Please select a CSV file');
        return;
      }
      setFile(selectedFile);
      setResult(null);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get(`/leads/chatbot/${chatbotId}/csv-template`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'leads_import_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a file');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('skip_duplicates', skipDuplicates.toString());
      const response = await api.post(
        `/leads/chatbot/${chatbotId}/import-csv`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      setResult(response.data);
      if (response.data.leads_created > 0) {
        toast.success(`Successfully imported ${response.data.leads_created} leads! 🎉`);
        onSuccess();
      } else if (response.data.leads_skipped > 0) {
        toast.info(`${response.data.leads_skipped} duplicate leads skipped`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setResult(null);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileUp className="w-5 h-5 text-blue-600" /> Import Leads from CSV</DialogTitle>
          <DialogDescription>Upload a CSV file to bulk import leads into your pipeline</DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          <div
            onClick={() => fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
              file ? 'border-emerald-300 bg-emerald-50' : 'border-slate-300 hover:border-blue-400 hover:bg-blue-50'
            }`}
          >
            <input ref={fileInputRef} type="file" accept=".csv" onChange={handleFileChange} className="hidden" />
            {file ? (
              <div className="flex flex-col items-center gap-2">
                <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center"><Check className="w-6 h-6 text-emerald-600" /></div>
                <p className="font-medium text-slate-900">{file.name}</p>
                <p className="text-sm text-slate-500">{(file.size / 1024).toFixed(1)} KB</p>
                <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setFile(null); }} className="text-red-600 hover:text-red-700">
                  <X className="w-4 h-4 mr-1" /> Remove
                </Button>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <Upload className="w-10 h-10 text-slate-400" />
                <p className="font-medium text-slate-700">Drop your CSV file here or click to browse</p>
                <p className="text-sm text-slate-500">Supports .csv files up to 10MB</p>
              </div>
            )}
          </div>

          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div>
              <Label className="text-slate-900 font-medium">Skip Duplicates</Label>
              <p className="text-sm text-slate-500">Skip leads with existing email addresses</p>
            </div>
            <Switch checked={skipDuplicates} onCheckedChange={setSkipDuplicates} />
          </div>

          <div className="flex items-center justify-between p-4 border border-slate-200 rounded-lg">
            <div>
              <p className="font-medium text-slate-900">Need a template?</p>
              <p className="text-sm text-slate-500">Download our CSV template with example data</p>
            </div>
            <Button variant="outline" size="sm" onClick={handleDownloadTemplate}>
              <Download className="w-4 h-4 mr-2" /> Template
            </Button>
          </div>

          {result && (
            <Card className={result.leads_created > 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}>
              <CardContent className="pt-4">
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${result.leads_created > 0 ? 'bg-emerald-100' : 'bg-amber-100'}`}>
                    {result.leads_created > 0 ? <CheckCircle className="w-5 h-5 text-emerald-600" /> : <AlertTriangle className="w-5 h-5 text-amber-600" />}
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-slate-900">Import Complete</h4>
                    <div className="text-sm text-slate-600 space-y-1 mt-1">
                      <p>✅ <strong>{result.leads_created}</strong> leads imported</p>
                      {result.leads_skipped > 0 && (<p>⏭️ <strong>{result.leads_skipped}</strong> duplicates skipped</p>)}
                      {result.total_errors > 0 && (<p>⚠️ <strong>{result.total_errors}</strong> errors</p>)}
                    </div>
                    {result.errors?.length > 0 && (
                      <div className="mt-2 p-2 bg-white/50 rounded text-xs text-slate-600">
                        {result.errors.slice(0, 3).map((err, idx) => (
                          <p key={`err-${err.slice ? err.slice(0, 40) : idx}-${idx}`}>{err}</p>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={handleClose}>{result ? 'Close' : 'Cancel'}</Button>
            {!result && (
              <Button onClick={handleUpload} disabled={!file || uploading} className="bg-blue-600 hover:bg-blue-700">
                {uploading ? (<><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Importing...</>) : (<><Upload className="w-4 h-4 mr-2" /> Import Leads</>)}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
